import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Autocomplete,
  Divider,
  Paper,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  CloudUpload as UploadIcon,
  Description as DocumentIcon,
  Delete as DeleteIcon,
  AttachMoney,
  CalendarMonth,
  Home,
  Person,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { ar, enUS } from 'date-fns/locale';
import { contractsApi, propertiesApi, profilesApi } from '../../../lib/api';
import { uploadContractDocument } from '../../../lib/imageUpload.web';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';

type PaymentFrequency = 'monthly' | 'quarterly' | 'biannually' | 'annually';

// Only include fields that exist in the contracts table
interface FormData {
  property_id: string;
  tenant_id: string;
  start_date: Date | null;
  end_date: Date | null;
  rent_amount: string;
  security_deposit: string;
  payment_frequency: PaymentFrequency;
  is_foreign_tenant: boolean;
}

export default function AddContract() {
  const { t } = useTranslation(['contracts', 'common']);
  const navigate = useNavigate();
  const language = useAppStore((state) => state.settings.language);
  const isRTL = language === 'ar';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<FormData>({
    property_id: '',
    tenant_id: '',
    start_date: null,
    end_date: null,
    rent_amount: '',
    security_deposit: '',
    payment_frequency: 'monthly',
    is_foreign_tenant: false,
  });

  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Fetch available properties
  const { data: properties, loading: propertiesLoading } = useApi(
    () => propertiesApi.getAll({ status: 'available' }),
    []
  );

  // Fetch tenants
  const { data: tenants, loading: tenantsLoading } = useApi(
    () => profilesApi.getTenants(),
    []
  );

  const handleChange = (field: keyof FormData) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleDateChange = (field: 'start_date' | 'end_date') => (date: Date | null) => {
    setFormData({ ...formData, [field]: date });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }

    // Auto-set end date 1 year from start date
    if (field === 'start_date' && date && !formData.end_date) {
      const endDate = new Date(date);
      endDate.setFullYear(endDate.getFullYear() + 1);
      setFormData((prev) => ({ ...prev, start_date: date, end_date: endDate }));
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, document: t('invalidDocumentType') });
        return;
      }

      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        setErrors({ ...errors, document: t('documentTooLarge') });
        return;
      }

      setDocumentFile(file);
      setErrors({ ...errors, document: '' });
    }
  };

  const handleRemoveDocument = () => {
    setDocumentFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.property_id) {
      newErrors.property_id = t('common:required');
    }

    if (!formData.tenant_id) {
      newErrors.tenant_id = t('common:required');
    }

    if (!formData.start_date) {
      newErrors.start_date = t('common:required');
    }

    if (!formData.end_date) {
      newErrors.end_date = t('common:required');
    }

    if (formData.start_date && formData.end_date && formData.end_date <= formData.start_date) {
      newErrors.end_date = t('endDateMustBeAfterStart');
    }

    if (!formData.rent_amount || isNaN(Number(formData.rent_amount)) || Number(formData.rent_amount) <= 0) {
      newErrors.rent_amount = t('invalidAmount');
    }

    if (!formData.security_deposit || isNaN(Number(formData.security_deposit))) {
      newErrors.security_deposit = t('invalidAmount');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError(null);

    try {
      let documentUrl: string | undefined;

      // Upload document if provided
      if (documentFile) {
        setUploadProgress(true);
        const uploadResult = await uploadContractDocument(documentFile);
        setUploadProgress(false);

        if (!uploadResult.success) {
          setSubmitError(uploadResult.error || t('documentUploadFailed'));
          setLoading(false);
          return;
        }
        documentUrl = uploadResult.url;
      }

      // Create contract - only include fields that exist in the database
      const contractData = {
        property_id: formData.property_id,
        tenant_id: formData.tenant_id,
        start_date: formData.start_date?.toISOString(),
        end_date: formData.end_date?.toISOString(),
        rent_amount: Number(formData.rent_amount),
        security_deposit: Number(formData.security_deposit),
        payment_frequency: formData.payment_frequency,
        is_foreign_tenant: formData.is_foreign_tenant,
        status: 'active' as const,
      };

      const response = await contractsApi.createWithDocument(contractData, documentUrl);

      if (response.error) {
        // Handle error object properly - extract message string
        const errorMessage = typeof response.error === 'string'
          ? response.error
          : response.error.message || t('common:errorOccurred');
        setSubmitError(errorMessage);
        return;
      }

      // Update property status to "rented" when contract is created
      if (formData.property_id) {
        try {
          await propertiesApi.update(formData.property_id, { status: 'rented' });
          console.log('Property status updated to rented');
        } catch (err) {
          console.error('Failed to update property status:', err);
          // Continue even if status update fails - contract was created successfully
        }
      }

      // Update tenant status to "active" when they have a contract
      if (formData.tenant_id) {
        try {
          await profilesApi.update(formData.tenant_id, { status: 'active' });
          console.log('Tenant status updated to active');
        } catch (err) {
          console.error('Failed to update tenant status:', err);
          // Continue even if status update fails - contract was created successfully
        }
      }

      // Navigate to contracts list on success
      navigate('/dashboard/contracts', {
        state: { message: t('contractCreatedSuccess') },
      });
    } catch (err: any) {
      setSubmitError(err.message || t('common:errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const BackArrow = isRTL ? ArrowForward : ArrowBack;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={isRTL ? ar : enUS}>
      <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)}>
            <BackArrow />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {t('addContract')}
          </Typography>
        </Box>

        {/* Error Alert */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        {/* Property & Tenant Selection */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Property Selection */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Home color="secondary" />
                  <Typography variant="h6">{t('selectProperty')}</Typography>
                </Box>
                <Autocomplete
                  options={properties || []}
                  getOptionLabel={(option: any) => `${option.title} - ${option.address}`}
                  loading={propertiesLoading}
                  onChange={(_, value) => setFormData({ ...formData, property_id: value?.id || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('property')}
                      error={!!errors.property_id}
                      helperText={errors.property_id}
                    />
                  )}
                />
              </Grid>

              {/* Tenant Selection */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Person color="info" />
                  <Typography variant="h6">{t('selectTenant')}</Typography>
                </Box>
                <Autocomplete
                  options={tenants || []}
                  getOptionLabel={(option: any) => `${option.first_name} ${option.last_name} - ${option.email}`}
                  loading={tenantsLoading}
                  onChange={(_, value) => setFormData({ ...formData, tenant_id: value?.id || '' })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('tenant')}
                      error={!!errors.tenant_id}
                      helperText={errors.tenant_id}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contract Duration */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CalendarMonth color="warning" />
              <Typography variant="h6">{t('contractDuration')}</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t('startDate')}
                  value={formData.start_date}
                  onChange={handleDateChange('start_date')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.start_date,
                      helperText: errors.start_date,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label={t('endDate')}
                  value={formData.end_date}
                  onChange={handleDateChange('end_date')}
                  minDate={formData.start_date || undefined}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.end_date,
                      helperText: errors.end_date,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_foreign_tenant}
                      onChange={handleChange('is_foreign_tenant')}
                    />
                  }
                  label={t('isForeignTenant')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Financial Terms */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoney color="success" />
              <Typography variant="h6">{t('financialTerms')}</Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('rentAmount')}
                  value={formData.rent_amount}
                  onChange={handleChange('rent_amount')}
                  type="number"
                  error={!!errors.rent_amount}
                  helperText={errors.rent_amount}
                  InputProps={{
                    endAdornment: <Typography color="text.secondary">SAR</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('securityDeposit')}
                  value={formData.security_deposit}
                  onChange={handleChange('security_deposit')}
                  type="number"
                  error={!!errors.security_deposit}
                  helperText={errors.security_deposit}
                  InputProps={{
                    endAdornment: <Typography color="text.secondary">SAR</Typography>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('paymentFrequency')}</InputLabel>
                  <Select
                    value={formData.payment_frequency}
                    label={t('paymentFrequency')}
                    onChange={handleChange('payment_frequency')}
                  >
                    <MenuItem value="monthly">{t('frequencyMonthly')}</MenuItem>
                    <MenuItem value="quarterly">{t('frequencyQuarterly')}</MenuItem>
                    <MenuItem value="biannually">{t('frequencyBiannually')}</MenuItem>
                    <MenuItem value="annually">{t('frequencyAnnually')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Contract Document Upload */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <UploadIcon color="primary" />
              <Typography variant="h6">{t('contractDocument')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('contractDocumentHelp')}
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="contract-document-input"
            />

            {documentFile ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: 'action.hover',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <DocumentIcon color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {documentFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(documentFile.size)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleRemoveDocument} color="error">
                  <DeleteIcon />
                </IconButton>
              </Paper>
            ) : (
              <Button
                variant="outlined"
                component="label"
                htmlFor="contract-document-input"
                startIcon={<UploadIcon />}
                fullWidth
                sx={{ py: 3, borderStyle: 'dashed' }}
              >
                {t('uploadDocument')}
              </Button>
            )}
            {errors.document && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {errors.document}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {uploadProgress ? t('uploadingDocument') : loading ? t('common:saving') : t('createContract')}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
